import {
  type BigFraction,
  bigFractionFromFraction,
} from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
} from "@aprao/common/src/math/fraction.js";
import type { FundingType } from "@aprao/common/src/shapes/common.js";
import range from "lodash/range";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";
import { UnreachableCaseError } from "ts-essentials";

import type {
  TimelineOverride,
  TimelineStage,
} from "../../shapes/TimelineStage.js";
import {
  getAtOffsetForDistribution,
  removeForDistribution,
  setForDistribution,
  toSortedForDistribution,
} from "./distribution.js";
import {
  type DistributionMethodName,
  distributionMethodsRegistry,
} from "./distributionMethodsRegistry.js";
import {
  containsForRange,
  defaultsForRange,
  type FrameRange,
  hasBoundsForRange,
  spanForRange,
} from "./FrameRange.js";

export class TimelineOverrideFacade {
  $model;

  constructor(model: TimelineOverride) {
    makeAutoObservable(this);

    this.$model = model;
  }

  get distributionMethod() {
    return this.$model.distributionMethod;
  }

  get frameRange() {
    return this.$model.frameRange;
  }

  get distribution() {
    return toSortedForDistribution(
      this.$model.distribution.map(([offset, ratio]) => [
        offset,
        bigFractionFromFraction(ratio),
      ]),
    );
  }
}

// Code duplication because extending a class doesn't work with Mobx's
// `makeAutoObservable` and it is the simplest workaround on this case.
// cf.https://github.com/mobxjs/mobx/discussions/2850
export class TimelineWritableOverrideFacade implements TimelineOverrideFacade {
  $model;

  constructor(model: TimelineOverride) {
    makeAutoObservable(this);

    this.$model = model;
  }

  get distributionMethod() {
    return this.$model.distributionMethod;
  }

  set distributionMethod(method: DistributionMethodName | null) {
    this.$model.distributionMethod = method;
  }

  get frameRange() {
    return this.$model.frameRange;
  }

  get distribution() {
    return toSortedForDistribution(
      this.$model.distribution.map(([offset, ratio]) => [
        offset,
        bigFractionFromFraction(ratio),
      ]),
    );
  }

  setDistributionAt(offset: number, ratio: BigFraction) {
    setForDistribution(this.$model.distribution, offset, ratio.toFraction());
  }

  removeDistributionAt(offset: number) {
    removeForDistribution(this.$model.distribution, offset);
  }
}

export interface TimelineItemAdapter<
  Facade extends TimelineOverrideFacade = TimelineOverrideFacade,
> {
  readonly currencyAmount: BigFraction | null;
  readonly fundingType: FundingType;
  readonly timelineOverride: Facade;
}

const computeItemDistributionMethod = (
  stage: TimelineStageAdapter,
  item: TimelineItemAdapter,
) => item.timelineOverride.distributionMethod ?? stage.distributionMethod;

export const computeItemRange = (
  stage: TimelineStageAdapter,
  item: TimelineItemAdapter,
) => defaultsForRange(item.timelineOverride.frameRange, stage.frameRange);

export interface CashflowItemRatio {
  readonly ratio: BigFraction;
}

export interface CashflowItemAmount {
  readonly amount: BigFraction;
}

export interface CashflowAmount {
  readonly amount: {
    readonly debtFunded: BigFraction;
    readonly forwardFunded: BigFraction;
  };
}

export const combineFundingAmounts = (amount: CashflowAmount) =>
  amount.amount.debtFunded.add(amount.amount.forwardFunded);

export class TimelineStageAdapter {
  $model;

  /**
   * A function providing the items involved in the timeline for the stage.
   */
  _items;

  constructor(model: TimelineStage, items: () => Array<TimelineItemAdapter>) {
    makeAutoObservable(this);

    this.$model = model;

    this._items = items;
  }

  get distributionMethod() {
    return this.$model.distributionMethod;
  }

  set distributionMethod(method: DistributionMethodName) {
    this.$model.distributionMethod = method;
  }

  get frameRange() {
    return this.$model.frameRange;
  }

  /**
   * The minimal range imposed by the overwritten frames and manual item's
   * distributions.
   */
  getMinimumFrameRangeForItem = (item: TimelineItemAdapter): FrameRange => {
    //const method = computeGroupDistributionMethod(this, group);

    const distribution =
      //method === "manual"
      //  ? item.manualDistribution :
      item.timelineOverride.distribution;

    const first = distribution.sorted.at(0);
    const last = distribution.sorted.at(-1);

    return {
      // If there are any negative offsets, they will impact the minimum start.
      // For example, if there is a distribution at -1, then the stage start
      // frame has to be at least 1
      lower: -(first?.[0] ?? 0),
      upper: (last?.[0] ?? 0) + 1 - (first?.[0] ?? 0),
    };
  };

  /**
   * Same as getMinimumFrameRangeForItem but for the whole stage.
   */
  get minimumFrameRange() {
    let frameRange: FrameRange = { lower: null, upper: null };

    for (const item of this._items()) {
      frameRange = spanForRange(
        frameRange,
        this.getMinimumFrameRangeForItem(item),
      );
    }

    return frameRange;
  }

  /**
   * The stage's time range once the overrides and manual groups are considered.
   */
  get computedFrameRange() {
    let frameRange = this.frameRange;

    for (const item of this._items()) {
      frameRange = spanForRange(item.timelineOverride.frameRange, frameRange);
    }

    return frameRange;
  }

  getBaselineCashflowAtFrameForItem = (
    item: TimelineItemAdapter,
    frameIndex: number,
  ): CashflowItemRatio | null => {
    const methodName = computeItemDistributionMethod(this, item);

    return mapForNullable(
      distributionMethodsRegistry[methodName](this, item, frameIndex),
      (ratio) => ({ ratio }),
    );
  };

  getTransposedOverwrittenDistributionForItem = (item: TimelineItemAdapter) => {
    const computedRange = computeItemRange(this, item);

    return item.timelineOverride.distribution.sorted
      .map(
        ([offset, ratio]) =>
          [offset + (computedRange.lower ?? 0), ratio] as [
            frameIndex: number,
            ratio: BigFraction,
          ],
      )
      .filter(([frameIndex]) => containsForRange(computedRange, frameIndex));
  };

  /**
   * Returns the sum of the ratio overrides for the given group.
   */
  getOverwrittenDistributionTotalForItem = (item: TimelineItemAdapter) =>
    this.getTransposedOverwrittenDistributionForItem(item).reduce(
      (total, [, ratio]) => total.add(ratio),
      FRACTION_ZERO,
    );

  /**
   * Return the adjustment factor for baseline distribution ratios (the ones
   * not overwritten) to ensure a total of 1 (100%) over the whole computed
   * distribution.
   */
  computeDistributionAdjustmentFactorForItem = computedFn(
    (item: TimelineItemAdapter) => {
      const computedRange = computeItemRange(this, item);

      // total of ratio left to assign for the baseline part
      const remainingAllocatable = FRACTION_ONE.sub(
        this.getOverwrittenDistributionTotalForItem(item),
      );

      // the list of frame indices that have been overwritten
      const overwrittenFrameIndex =
        this.getTransposedOverwrittenDistributionForItem(item).map(
          ([frameIndex]) => frameIndex,
        );

      // Compute the sum of distribution ratios that don't have an override.
      //
      // So if the initial distribution yields `[0.1, 0.1, 0.8]` and the first frame
      // is overwritten, it would return 0.9 (0.1 + 0.8).
      let sumBaseline = FRACTION_ZERO;
      for (const frameIndex of range(
        computedRange.lower ?? 0,
        computedRange.upper ?? 0,
      )) {
        if (!overwrittenFrameIndex.includes(frameIndex)) {
          sumBaseline = sumBaseline.add(
            this.getBaselineCashflowAtFrameForItem(item, frameIndex)?.ratio ??
              FRACTION_ZERO,
          );
        }
      }

      return sumBaseline.equals(FRACTION_ZERO) ? FRACTION_ONE : (
          remainingAllocatable.div(sumBaseline)
        );
    },
  );

  getCashflowOverwriteAtFrameForItem = (
    item: TimelineItemAdapter,
    frameIndex: number,
  ): CashflowItemRatio | null => {
    const computedRange = computeItemRange(this, item);
    if (
      !hasBoundsForRange(computedRange) ||
      !containsForRange(computedRange, frameIndex)
    ) {
      return null;
    }

    const ratioOverwrite = getAtOffsetForDistribution(
      item.timelineOverride.distribution,
      frameIndex - computedRange.lower,
    );

    return mapForNullable(ratioOverwrite, (ratio) => {
      return { ratio };
    });
  };

  getCashflowAtFrameForItem = (
    item: TimelineItemAdapter,
    frameIndex: number,
  ): CashflowItemRatio | null => {
    const overwrite = this.getCashflowOverwriteAtFrameForItem(item, frameIndex);

    if (overwrite != null) {
      return overwrite;
    }

    const adjustmentFactor =
      this.computeDistributionAdjustmentFactorForItem(item);

    const baselineRatio = mapForNullable(
      this.getBaselineCashflowAtFrameForItem(item, frameIndex),
      ({ ratio }) => ratio.mul(adjustmentFactor),
    );

    return mapForNullable(baselineRatio, (ratio) => ({
      ratio,
    }));
  };

  getStageCashflowAtFrame = (
    frameIndex: number,
    direction: "inflow" | "outflow",
  ): CashflowAmount => {
    const sum = {
      amount: {
        debtFunded: FRACTION_ZERO,
        forwardFunded: FRACTION_ZERO,
      },
    };

    for (const item of this._items()) {
      const groupCashflow = this.getCashflowAtFrameForItem(item, frameIndex);

      if (
        groupCashflow != null &&
        item.currencyAmount != null &&
        // We filter out to only include the direction required. It is to
        // properly manage opposite flows (like costs in the revenue section)
        // when distributed inside a stage (rather than the typical "a single
        // flow direction per stage")
        (direction === "inflow") !== item.currencyAmount.isNeg()
      ) {
        const amount = groupCashflow.ratio.mul(item.currencyAmount);
        if (item.fundingType === "debtFunded") {
          sum.amount.debtFunded = sum.amount.debtFunded.add(amount);
        } else if (item.fundingType === "forwardFunded") {
          sum.amount.forwardFunded = sum.amount.forwardFunded.add(amount);
        } else {
          throw new UnreachableCaseError(item.fundingType);
        }
      }
    }

    return sum;
  };

  getAggregatedCashflowForItem = (
    item: TimelineItemAdapter,
  ): CashflowItemAmount => {
    const aggregatedCashflow = {
      amount: FRACTION_ZERO,
    };

    for (const frameIndex of range(
      this.computedFrameRange.lower ?? 0,
      // this range function is inclusive, no need to do +1
      // cf. https://radash-docs.vercel.app/docs/array/range
      this.computedFrameRange.upper ?? 0,
    )) {
      const cashflow = this.getCashflowAtFrameForItem(item, frameIndex);
      if (cashflow != null && item.currencyAmount != null) {
        aggregatedCashflow.amount = aggregatedCashflow.amount.add(
          cashflow.ratio.mul(item.currencyAmount),
        );
      }
    }

    return aggregatedCashflow;
  };

  getStageAggregatedCashflow = (
    direction: "inflow" | "outflow",
  ): CashflowAmount => {
    const aggregatedCashflow = {
      amount: {
        debtFunded: FRACTION_ZERO,
        forwardFunded: FRACTION_ZERO,
      },
    };

    for (const frameIndex of range(
      this.computedFrameRange.lower ?? 0,
      this.computedFrameRange.upper ?? 0,
    )) {
      const frameCashflow = this.getStageCashflowAtFrame(frameIndex, direction);
      aggregatedCashflow.amount.debtFunded =
        aggregatedCashflow.amount.debtFunded.add(
          frameCashflow.amount.debtFunded,
        );
      aggregatedCashflow.amount.forwardFunded =
        aggregatedCashflow.amount.forwardFunded.add(
          frameCashflow.amount.forwardFunded,
        );
    }

    return aggregatedCashflow;
  };
}
